/**
 * Composer text ↔ mention chips, shared by every platform. The draft stays the token string the server parser
 * (apps/api/src/richtext/parse.ts) reads — `@Display Name`, `@Full Name`, `@handle`, `<@userId>`, `#channel-name`,
 * `<#channelId>`, `@here` / `<!here>` — so sending, drafts and the message rendering don't change. The field shows each
 * token the parser would resolve as one atomic chip (one U+FFFC character in the field's text, labelled like the
 * message chip: `@Ada Lovelace`, `#design`, `@here`, "self" when it's me), and edits made in the field's text are
 * mapped back onto the draft.
 *
 * Recognition mirrors the parser: angle forms first, `@` and `#` only at a word boundary (`ana@acme.com` stays text),
 * member names longest-first and not followed by a word character, nothing inside `code` / ``` spans or URLs.
 * A typed name chips as soon as it resolves (the server would mention it too); typing on turns it back into text.
 *
 * Offsets: "text" offsets are UTF-16 offsets in the draft; "view" offsets are in the field's text, where a chip is
 * one character. An offset inside a token maps to the chip's end.
 */
import type { Channel, Member, UserGroup } from "@bundle/contracts";
import type { BroadcastRange, TextEdit } from "./autocomplete.ts";
import type { ChipKind } from "./richtext.ts";
import { groupIncludes, isGroupActive } from "./userGroups.ts";

/** The one character a chip takes in the field's text. */
export const CHIP_CHAR = "￼";

export type ComposerChipContext = {
  meId: string;
  members: Record<string, Member>;
  channels: Record<string, Channel>;
  /** User groups: `@handle` and `<!subteam^id>` of an active group chip like the message does. */
  userGroups?: Record<string, UserGroup>;
};

/** [start, end) of the draft: plain text, or one token shown as a chip. */
export type ComposerSegment =
  | { type: "text"; text: string; start: number; end: number }
  | {
      type: "chip";
      /** Message chip look (logic/richtext): user, agent, self (me), channel, broadcast. */
      chip: ChipKind;
      /** What the chip shows: `@Display Name`, `#channel`, `@here`. */
      label: string;
      /** The draft text it stands for. */
      token: string;
      start: number;
      end: number;
      userId?: string;
      channelId?: string;
      range?: BroadcastRange;
      groupId?: string;
    };

type Chip = Extract<ComposerSegment, { type: "chip" }>;

const WORD = /[\p{L}\p{N}_]/u;
const isWord = (c: string): boolean => c !== "" && WORD.test(c);
const URL_RE = /^(?:https?:\/\/|mailto:)[^\s<>]+/i;
const ANGLE_RE = /^<([^<>\n]+)>/;
const MD_LINK_RE = /^\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s()]+(?:\([^\s()]*\)[^\s()]*)*)\)/i;
const CHANNEL_RE = /^#([\p{L}\p{N}][\p{L}\p{N}_-]*)/u;
const BROADCAST_RE = /^(here|channel|everyone)(?![\p{L}\p{N}_])/iu;
const RAW_ID_RE = /^([0-9A-Z]{26})(?![\p{L}\p{N}_])/u;

type Lookup = { names: { name: string; userId: string; groupId?: string }[]; channelsByName: Map<string, string> };

/** Same directory as the server's: active members' display, full and user names; public and private channels. */
function lookupOf(ctx: ComposerChipContext): Lookup {
  const names: Lookup["names"] = [];
  for (const id of Object.keys(ctx.members)) {
    const m = ctx.members[id]!;
    if (m.deactivated) continue;
    for (const n of [m.displayName, m.fullName, m.handle]) {
      const name = n?.trim().toLowerCase();
      if (name !== undefined && name !== "") names.push({ name, userId: id });
    }
  }
  for (const id of Object.keys(ctx.userGroups ?? {})) {
    const g = ctx.userGroups![id]!;
    if (isGroupActive(g) && g.handle !== "") names.push({ name: g.handle.toLowerCase(), userId: "", groupId: id });
  }
  names.sort((a, b) => b.name.length - a.name.length || (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0));
  const channelsByName = new Map<string, string>();
  for (const id of Object.keys(ctx.channels)) {
    const c = ctx.channels[id]!;
    if ((c.kind === "public" || c.kind === "private") && c.name !== "") channelsByName.set(c.name.toLowerCase(), id);
  }
  return { names, channelsByName };
}

function userChip(ctx: ComposerChipContext, userId: string, token: string, start: number): Chip | undefined {
  const m = ctx.members[userId];
  if (m === undefined) return undefined;
  const chip: ChipKind = userId === ctx.meId ? "self" : m.kind === "agent" ? "agent" : "user";
  return { type: "chip", chip, label: `@${m.displayName}`, token, start, end: start + token.length, userId };
}

function channelChip(ctx: ComposerChipContext, channelId: string, token: string, start: number): Chip | undefined {
  const c = ctx.channels[channelId];
  if (c === undefined) return undefined;
  return { type: "chip", chip: "channel", label: `#${c.name}`, token, start, end: start + token.length, channelId };
}

function groupChip(ctx: ComposerChipContext, groupId: string, token: string, start: number): Chip | undefined {
  const g = ctx.userGroups?.[groupId];
  if (g === undefined || !isGroupActive(g)) return undefined;
  return { type: "chip", chip: groupIncludes(g, ctx.meId) ? "self" : "group", label: `@${g.handle}`, token, start, end: start + token.length, groupId };
}

function broadcastChip(range: BroadcastRange, token: string, start: number): Chip {
  return { type: "chip", chip: "broadcast", label: `@${range}`, token, start, end: start + token.length, range };
}

/** `<@id>`, `<@id|name>`, `<#id>`, `<#name>`, `<!here>` at `i`; links and unknown targets aren't chips. */
function angleChip(text: string, i: number, ctx: ComposerChipContext, lk: Lookup): Chip | undefined {
  const m = ANGLE_RE.exec(text.slice(i));
  if (m === null) return undefined;
  const inner = m[1]!;
  const bar = inner.indexOf("|");
  const target = bar < 0 ? inner : inner.slice(0, bar);
  if (target.startsWith("@")) return userChip(ctx, target.slice(1), m[0], i);
  if (target.startsWith("#")) {
    const id = target.slice(1);
    const byId = ctx.channels[id];
    if (byId !== undefined && (byId.kind === "public" || byId.kind === "private")) return channelChip(ctx, id, m[0], i);
    const byName = lk.channelsByName.get(id.toLowerCase());
    return byName === undefined ? undefined : channelChip(ctx, byName, m[0], i);
  }
  if (target.startsWith("!subteam^")) return groupChip(ctx, target.slice("!subteam^".length), m[0], i);
  if (target.startsWith("!")) {
    const range = target.slice(1).toLowerCase();
    return range === "here" || range === "channel" || range === "everyone" ? broadcastChip(range, m[0], i) : undefined;
  }
  return undefined;
}

/** `@name` (longest member name first, so a member named "Here" beats the broadcast), `@here`, `@<raw id>`. */
function atChip(text: string, i: number, ctx: ComposerChipContext, lk: Lookup): Chip | undefined {
  const tail = text.slice(i + 1);
  const lower = tail.toLowerCase();
  for (const { name, userId, groupId } of lk.names) {
    if (!lower.startsWith(name) || isWord(tail.charAt(name.length))) continue;
    const token = text.slice(i, i + 1 + name.length);
    return groupId === undefined ? userChip(ctx, userId, token, i) : groupChip(ctx, groupId, token, i);
  }
  const b = BROADCAST_RE.exec(tail);
  if (b !== null) return broadcastChip(b[1]!.toLowerCase() as BroadcastRange, text.slice(i, i + 1 + b[0].length), i);
  const id = RAW_ID_RE.exec(tail);
  if (id !== null && ctx.members[id[1]!] !== undefined) return userChip(ctx, id[1]!, text.slice(i, i + 1 + id[0].length), i);
  return undefined;
}

/** Length of the code span (`x` on one line, or ```…```) opening at `i`, or 0 when it isn't closed. */
function codeSpan(text: string, i: number): number {
  const fence = text.startsWith("```", i) ? "```" : "`";
  const end = text.indexOf(fence, i + fence.length);
  if (end < 0) return 0;
  const inner = text.slice(i + fence.length, end);
  if (inner.length === 0 || (fence === "`" && inner.includes("\n"))) return 0;
  return end + fence.length - i;
}

/** URLs end where the parser's autolinks do (trailing punctuation isn't part of them). */
function urlLength(text: string, i: number): number {
  const m = URL_RE.exec(text.slice(i));
  if (m === null) return 0;
  let u = m[0];
  for (;;) {
    const last = u.charAt(u.length - 1);
    if (last !== "" && ".,;:!?'\"*_~".includes(last)) u = u.slice(0, -1);
    else if (last === ")" && u.split("(").length < u.split(")").length) u = u.slice(0, -1);
    else break;
  }
  return /^(?:https?:\/\/[^/\s]+|mailto:.+)/i.test(u) ? u.length : 0;
}

/** The draft as text and chip segments, in order, covering all of it (empty text = no segments). */
export function composerSegments(text: string, ctx: ComposerChipContext): ComposerSegment[] {
  const lk = lookupOf(ctx);
  const out: ComposerSegment[] = [];
  let textStart = 0;
  const flush = (end: number) => {
    if (end > textStart) out.push({ type: "text", text: text.slice(textStart, end), start: textStart, end });
  };
  let i = 0;
  while (i < text.length) {
    const c = text.charAt(i);
    const prev = i > 0 ? text.charAt(i - 1) : "";
    const boundary = !isWord(prev);
    let chip: Chip | undefined;
    let skip = 0;
    if (c === "`") skip = codeSpan(text, i);
    else if (c === "<") {
      chip = angleChip(text, i, ctx, lk);
      if (chip === undefined) {
        const m = ANGLE_RE.exec(text.slice(i));
        if (m !== null && /^(?:https?:\/\/|mailto:)\S+$/i.test(m[1]!.split("|")[0]!)) skip = m[0].length;
      }
    } else if (c === "[") {
      const m = MD_LINK_RE.exec(text.slice(i));
      if (m !== null) skip = m[0].length;
    } else if ((c === "h" || c === "H" || c === "m" || c === "M") && boundary) skip = urlLength(text, i);
    else if (c === "@" && boundary) chip = atChip(text, i, ctx, lk);
    else if (c === "#" && boundary && prev !== "&") {
      const m = CHANNEL_RE.exec(text.slice(i));
      const id = m === null ? undefined : lk.channelsByName.get(m[1]!.toLowerCase());
      if (m !== null && id !== undefined) chip = channelChip(ctx, id, m[0], i);
    }
    if (chip !== undefined) {
      flush(i);
      out.push(chip);
      i = chip.end;
      textStart = i;
    } else i += skip > 0 ? skip : 1;
  }
  flush(text.length);
  return out;
}

/** The field's text: each chip is one CHIP_CHAR. */
export function viewText(segments: ComposerSegment[]): string {
  return segments.map((s) => (s.type === "chip" ? CHIP_CHAR : s.text)).join("");
}

/** Draft offset → field offset (inside a token = after its chip). */
export function toViewOffset(segments: ComposerSegment[], offset: number): number {
  let view = 0;
  for (const s of segments) {
    if (offset <= s.start) return view;
    if (s.type === "text") {
      if (offset <= s.end) return view + (offset - s.start);
      view += s.end - s.start;
    } else {
      view += 1;
      if (offset <= s.end) return view;
    }
  }
  return view;
}

/** Field offset → draft offset (before a chip = its token's start, after it = its end). */
export function toTextOffset(segments: ComposerSegment[], offset: number): number {
  let view = 0;
  let end = 0;
  for (const s of segments) {
    const len = s.type === "chip" ? 1 : s.end - s.start;
    if (offset <= view) return s.start;
    if (offset < view + len) return s.start + (offset - view);
    view += len;
    end = s.end;
    if (offset === view) return s.end;
  }
  return end;
}

/**
 * Applies a field edit ([start, end) of the field's text replaced by `inserted`) to the draft. Deleting a chip deletes
 * its whole token; chip characters in `inserted` (a chip dragged or copied inside the field) are dropped.
 */
export function applyViewEdit(text: string, segments: ComposerSegment[], start: number, end: number, inserted: string): TextEdit {
  const a = toTextOffset(segments, Math.min(start, end));
  const b = Math.max(a, toTextOffset(segments, Math.max(start, end)));
  const ins = inserted.split(CHIP_CHAR).join("");
  return { text: `${text.slice(0, a)}${ins}${text.slice(b)}`, caret: a + ins.length };
}
